import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DialogBox } from "../../../../../components/Elements/DialogBox";
import {
  fetchLinkedItems,
  fetchAllLocations,
  createMapping,
  clearMappingStatus,
} from "../../../../../redux/OrganizationSetup/organizationSetupSlice";
import PopupCard from "../../../../Auth/PopupCard";
import { createPortal } from "react-dom";

const LinkLocationPopup = ({
  onOpen,
  onClose,
  departmentName,
  departmentId,
}) => {
  const dispatch = useDispatch();
  const {
    linkedItems,
    locationList,
    loadingParams,
    mappingSuccess,
    mappingError,
    mappingContext,
  } = useSelector((state) => state.orgSetup);
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    if (onOpen && departmentId) {
      dispatch(fetchLinkedItems(departmentId));
      dispatch(fetchAllLocations());
    }
  }, [onOpen, departmentId, dispatch]);

  useEffect(() => {
    if (linkedItems && Array.isArray(linkedItems)) {
      setSelectedLocations(
        linkedItems.filter((item) => item && item._id).map((item) => item._id),
      );
    } else {
      setSelectedLocations([]);
    }
  }, [linkedItems]);

  // Handle Success Auto-Close
  useEffect(() => {
    if (mappingSuccess && mappingContext === "departmentLocations") {
      const timer = setTimeout(() => {
        dispatch(clearMappingStatus());
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mappingSuccess, mappingContext, dispatch, onClose]);

  useEffect(() => {
    if (mappingError && mappingContext === "departmentLocations") {
      const timer = setTimeout(() => {
        dispatch(clearMappingStatus());
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [mappingError, mappingContext, dispatch]);

  // Use the global location list from Redux
  const allLocations = (locationList || []).filter((loc) => loc && loc._id);

  const handleCheckboxChange = (locationId) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const handleSave = () => {
    const payload = {
      parentId: departmentId,
      childIds: selectedLocations,
      mappingContext: "departmentLocations",
    };
    dispatch(createMapping(payload));
  };

  return (
    <DialogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="750px">
      <div className="p-8 relative">
        {/* Back Button */}
        {/* <div
                    onClick={onClose}
                    className="absolute top-6 right-6 w-8 h-8 rounded-full border-2 border-[#06513D] flex items-center justify-center cursor-pointer text-[#06513D] hover:bg-[#06513D] hover:text-white transition-all shadow-sm"
                >
                </div> */}

        {/* Title */}
        <h2 className="text-[22px] font-bold text-[#06513D] mb-8">
          Add Location
        </h2>

        {/* Checkbox Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-8 mb-12 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
          {[...allLocations]
            .sort((a, b) => {
              // Get the index in the already linked items list
              const aIndex = (linkedItems || []).findIndex(
                (item) => item?._id === a._id,
              );
              const bIndex = (linkedItems || []).some(
                (item) => item?._id === b._id,
              ); // Just checking if B is linked

              // If both are linked, sort by their index in the linkedItems array
              if (
                aIndex !== -1 &&
                (linkedItems || []).some((item) => item?._id === b._id)
              ) {
                const bIndexActual = (linkedItems || []).findIndex(
                  (item) => item?._id === b._id,
                );
                return aIndex - bIndexActual;
              }

              // Otherwise, just ensure linked ones are at the top
              const aIsLinked = aIndex !== -1;
              const bIsLinked = (linkedItems || []).some(
                (item) => item?._id === b._id,
              );
              return bIsLinked - aIsLinked;
            })
            .map((loc) => (
              <div key={loc._id} className="flex items-center gap-3 min-w-0">
                <input
                  type="checkbox"
                  id={loc._id}
                  checked={selectedLocations.includes(loc._id)}
                  onChange={() => handleCheckboxChange(loc._id)}
                  className="w-5 h-5 flex-shrink-0 rounded-md border-[#3BA2B959] cursor-pointer accent-[#06513D]"
                />
                <label
                  htmlFor={loc._id}
                  className="min-w-0 flex-1 text-[15px] sm:text-[16px] leading-5 text-[#2D2D2D] font-medium cursor-pointer select-none break-words"
                >
                  {loc.name || loc.location_name}
                </label>
              </div>
            ))}
          {allLocations.length === 0 &&
            !loadingParams.fetchLinked &&
            !loadingParams.fetchLocation && (
              <p className="text-gray-400 italic">No locations available</p>
            )}
          {(loadingParams.fetchLinked || loadingParams.fetchLocation) && (
            <p className="text-[#06513D] animate-pulse">Loading locations...</p>
          )}
        </div>

        {/* Success/Error Popups */}
        {mappingSuccess &&
          mappingContext === "departmentLocations" &&
          createPortal(
            <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
              <PopupCard
                type="success"
                title="Success"
                message="Location and Department linked successfully!"
              />
            </div>,
            document.body,
          )}

        {mappingError &&
          mappingContext === "departmentLocations" &&
          createPortal(
            <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
              <PopupCard type="error" title="Error" message={mappingError} />
            </div>,
            document.body,
          )}

        {/* Footer Buttons */}
        <div className="flex justify-end gap-4 pt-2">
          <button
            onClick={onClose}
            className="min-w-[120px] px-8 py-2.5 border border-[#E5EAEF] text-[#2D2D2D] font-medium rounded-xl text-[16px] hover:bg-gray-50 transition-colors shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loadingParams?.createMapping}
            className={`min-w-[120px] px-10 py-2.5 bg-[#06513D] text-white rounded-xl text-[16px] font-bold transition-all shadow-sm ${
              loadingParams?.createMapping
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#054131] hover:shadow-md"
            }`}
          >
            {loadingParams?.createMapping ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </DialogBox>
  );
};

export default LinkLocationPopup;
